import { z } from "zod";
import { getAttribution } from "@/lib/attribution";
import { hasConsent } from "@/lib/cookie-consent";

/** Campaign tag attached to every checkout start (validated server-side). */
export const checkoutAttributionSchema = z
  .object({
    utm_source: z.string().max(200).nullish(),
    utm_medium: z.string().max(200).nullish(),
    utm_campaign: z.string().max(200).nullish(),
    utm_content: z.string().max(200).nullish(),
    utm_term: z.string().max(200).nullish(),
    platform: z.string().max(60).nullish(),
    referrer: z.string().max(500).nullish(),
    landing_path: z.string().max(300).nullish(),
  })
  .nullish();

export type CheckoutAttribution = z.infer<typeof checkoutAttributionSchema>;

/**
 * Reads the visitor's first-party campaign tag (captured on landing) for a
 * checkout. Honours analytics consent — returns undefined without it.
 */
export function checkoutAttribution(): CheckoutAttribution | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    if (!hasConsent("analytics")) return undefined;
    const a = getAttribution();
    if (!a) return undefined;
    let referrerHost: string | null = null;
    if (a.referrer) {
      try {
        referrerHost = new URL(a.referrer).hostname;
      } catch {
        referrerHost = null;
      }
    }
    return {
      utm_source: a.utm_source,
      utm_medium: a.utm_medium,
      utm_campaign: a.utm_campaign,
      utm_content: a.utm_content,
      utm_term: a.utm_term,
      platform: a.platform,
      referrer: referrerHost,
      landing_path: a.landing_path,
    };
  } catch {
    return undefined;
  }
}
